import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Building2, FolderKanban, Loader2, Package, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { useCompanyList } from "@/modules/companies/hooks/useCompanies";
import { useProductList } from "@/modules/products/hooks/useProducts";
import { useProjectList } from "@/modules/projects/hooks/useProjects";
import { useCategoryItems } from "@/modules/dropdowns/hooks/useDropdowns";
import { useCreateEstimation } from "../hooks/useEstimations";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: (id: string, code: string) => void;
};

export function NewEstimationDrawer({ isOpen, onClose, onCreated }: Props) {
  const createEstimation = useCreateEstimation();
  const { data: currencyItems = [] } = useCategoryItems("currency");

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [currency, setCurrency] = useState("AED");
  const [contingencyPct, setContingencyPct] = useState("15");
  const [selectedCompanyId, setSelectedCompanyId] = useState("");
  const [selectedProductId, setSelectedProductId] = useState("");
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setError(null);
  }, [isOpen]);

  const { data: companiesResult } = useCompanyList({
    page: 1,
    limit: 100,
    operationalOnly: true,
  });
  const companies = companiesResult?.data ?? [];

  const { data: productsResult } = useProductList({
    page: 1,
    limit: 100,
    companyId: selectedCompanyId || undefined,
  });
  const products = productsResult?.data ?? [];

  const { data: projectsResult } = useProjectList({
    page: 1,
    limit: 100,
    companyId: selectedCompanyId || undefined,
    productId: selectedProductId || undefined,
  });
  const projects = projectsResult?.data ?? [];

  function reset() {
    setName("");
    setDescription("");
    setCurrency("AED");
    setContingencyPct("15");
    setSelectedCompanyId("");
    setSelectedProductId("");
    setSelectedProjectId("");
    setNotes("");
    setError(null);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    const pct =
      contingencyPct.trim() === "" ? null : Number(contingencyPct);
    if (pct !== null && (!Number.isFinite(pct) || pct < 0 || pct > 100)) {
      setError("Contingency % must be between 0 and 100");
      return;
    }
    setError(null);
    try {
      const res = await createEstimation.mutateAsync({
        name: name.trim(),
        description: description.trim() || null,
        currency,
        contingencyPct: pct,
        companyId: selectedCompanyId || null,
        productId: selectedProductId || null,
        projectId: selectedProjectId || null,
        notes: notes.trim() || null,
      });
      onCreated?.(res.estimation.id, res.estimation.code);
      reset();
      onClose();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to create estimation",
      );
    }
  }

  if (!isOpen) return null;

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-[9998] bg-black/30"
        onClick={onClose}
      />
      <div
        className="fixed right-0 top-0 z-[9999] flex h-full w-[500px] flex-col border-l bg-white shadow-xl"
        style={{ borderColor: "hsl(210,30%,85%)" }}
      >
        <div
          className="flex items-center justify-between border-b px-6 py-4"
          style={{ borderColor: "hsl(210,30%,85%)" }}
        >
          <h2 className="text-lg font-semibold text-foreground">
            New Cost Estimation
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-[hsl(210,50%,96%)]"
          >
            <X className="h-4 w-4" strokeWidth={1.5} />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="flex flex-1 flex-col overflow-y-auto"
        >
          <div className="flex-1 space-y-6 p-6">
            <h3 className="text-sm font-semibold text-foreground">Details</h3>

            <div className="space-y-2">
              <Label htmlFor="est-name">
                Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="est-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Q3 Platform Build Estimate"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="est-desc">Description</Label>
              <textarea
                id="est-desc"
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="flex w-full rounded-md border border-border bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="est-currency">Currency</Label>
                <Select
                  id="est-currency"
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                >
                  {currencyItems.length === 0 ? (
                    <option value="AED">AED</option>
                  ) : (
                    currencyItems.map((c) => (
                      <option key={c.id} value={c.value.toUpperCase()}>
                        {c.label}
                      </option>
                    ))
                  )}
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="est-contingency">Contingency %</Label>
                <Input
                  id="est-contingency"
                  type="number"
                  step="0.5"
                  min="0"
                  max="100"
                  value={contingencyPct}
                  onChange={(e) => setContingencyPct(e.target.value)}
                />
              </div>
            </div>

            <h3 className="text-sm font-semibold text-foreground">
              Association
            </h3>

            <div className="space-y-2">
              <Label htmlFor="est-company">Company</Label>
              <div className="relative">
                <Building2
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                  strokeWidth={1.5}
                />
                <select
                  id="est-company"
                  value={selectedCompanyId}
                  onChange={(e) => {
                    setSelectedCompanyId(e.target.value);
                    setSelectedProductId("");
                    setSelectedProjectId("");
                  }}
                  className="flex h-9 w-full rounded-md border border-border bg-background pl-9 pr-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                >
                  <option value="">No company</option>
                  {companies.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name} ({c.code})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="est-product">Product</Label>
              <div className="relative">
                <Package
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                  strokeWidth={1.5}
                />
                <select
                  id="est-product"
                  value={selectedProductId}
                  onChange={(e) => {
                    setSelectedProductId(e.target.value);
                    setSelectedProjectId("");
                  }}
                  className="flex h-9 w-full rounded-md border border-border bg-background pl-9 pr-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                >
                  <option value="">No product</option>
                  {products.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} ({p.code})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="est-project">Project</Label>
              <div className="relative">
                <FolderKanban
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                  strokeWidth={1.5}
                />
                <select
                  id="est-project"
                  value={selectedProjectId}
                  onChange={(e) => setSelectedProjectId(e.target.value)}
                  className="flex h-9 w-full rounded-md border border-border bg-background pl-9 pr-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                >
                  <option value="">No project</option>
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} ({p.code})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="est-notes">Notes</Label>
              <textarea
                id="est-notes"
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="flex w-full rounded-md border border-border bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
              />
            </div>

            {error && (
              <div
                className="rounded-md border px-3 py-2 text-sm"
                style={{
                  borderColor: "hsl(0,70%,80%)",
                  background: "hsl(0,70%,96%)",
                  color: "hsl(0,70%,35%)",
                }}
              >
                {error}
              </div>
            )}
          </div>

          <div
            className="flex items-center justify-end gap-3 border-t px-6 py-4"
            style={{ borderColor: "hsl(210,30%,85%)" }}
          >
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!name.trim() || createEstimation.isPending}
            >
              {createEstimation.isPending && (
                <Loader2
                  className="mr-1 h-4 w-4 animate-spin"
                  strokeWidth={1.5}
                />
              )}
              Create Estimation
            </Button>
          </div>
        </form>
      </div>
    </>,
    document.body,
  );
}
